import { Option, QuestionGroup, CandidateRoleAnswerModel } from "../Models";
import { APIURL } from "./config";

export const putAssessmentQuestion = async (
  id: string | undefined,
  text: string,
  time: string,
  type: string,
  tags: string,
  fieldName: string | null,
  questionGroupId: QuestionGroup | undefined,
  comprehensiveQuestionId: string | null,
  token: string,
  parentQuestionId?: string | null,
  setupScript?: string | null,
  expectedQuery?: string | null,
  initialCode?: string | null,
  testCases?: string | null,
  imageUrl?: string | null
) => {
  await fetch(`${APIURL}/AssessmentQuestion/${id}`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      id: id,
      text: text,
      time: time,
      type: type.toLowerCase(),
      tags: tags,
      fieldName: fieldName,
      questionGroupId: questionGroupId?.id ?? null,
      comprehensiveQuestionId: comprehensiveQuestionId,
      parentQuestionId: parentQuestionId,
      setupScript: setupScript,
      expectedQuery: expectedQuery,
      initialCode: initialCode,
      testCases: testCases,
      imageUrl: imageUrl,
    }),
  });
};

export const getQuestionById = async (qId: string, token: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestion/questions/${qId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
};

export const postQuestion = async (
  text: string,
  time: string,
  tags: string,
  fieldName: string | null,
  selectedQGroupId: string | null,
  qType: string,
  options: Option[] | undefined,
  comprehensiveQuestionId: string | null,
  token: string,
  parentQuestionId?: string | null,
  setupScript?: string | null,
  expectedQuery?: string | null,
  initialCode?: string | null,
  testCases?: string | null,
  imageUrl?: string | null
) => {
  const res = await fetch(`${APIURL}/AssessmentQuestion`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      text,
      time: parseInt(time),
      tags,
      fieldName,
      questionGroupId: selectedQGroupId,
      type: qType.toLowerCase(),
      comprehensiveQuestionId: comprehensiveQuestionId,
      parentQuestionId: parentQuestionId,
      setupScript: setupScript,
      expectedQuery: expectedQuery,
      initialCode: initialCode,
      testCases: testCases,
      imageUrl: imageUrl,
    }),
  });
  const json = await res.json();
  const { id } = json;

  if (options)
    for (let index = 0; index < options.length; index++) {
      const i = options[index];
      await fetch(`${APIURL}/AssessmentQuestionAnswer`, {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          questionId: id,
          text: i.text,
          isCorrect: i.isCorrect,
          order: index,
          imageUrl: i.imageUrl,
        }),
      });
    }
  return json;
};

export const getAllQuestions = async (
  token: string,
  currentPage?: number,
  searchQuery = "",
) => {
  if (currentPage) {
    const res = await fetch(
      `${APIURL}/AssessmentQuestion?pageNumber=${currentPage}&searchQuery=${searchQuery}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const json = res.json();
    return json;
  } else {
    const res = await fetch(
      `${APIURL}/AssessmentQuestion?searchQuery=${searchQuery}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    const json = res.json();
    return json;
  }
};

export const getAllFilterQuestions = async (
  token: string,
  accuracy: { min: number; max: number },
  qGids?: string[],
  selectedSkills?: string[],
  selectedAssessments?: string[],
  selectedType?: string[],
  currentPage?: number,
  searchQuery = "",
  sortOptions: { field: string; direction: "asc" | "desc" | "default" }[] = []
) => {
  const { min, max } = accuracy;

  let url = `${APIURL}/AssessmentQuestion/filtered`;

  let queryParams = `?MinAcc=${min}&MaxAcc=${max}`;

  if (currentPage) {
    queryParams += `&pageNumber=${currentPage}`;
  }

  if (searchQuery) {
    queryParams += `&searchQuery=${searchQuery}`;
  }

  const textSort = sortOptions.find((option) => option.field === "text");
  const accuracySort = sortOptions.find(
    (option) => option.field === "accuracy"
  );

  queryParams += `&sortByText=${textSort?.direction || "default"}`;
  queryParams += `&sortByAccuracy=${accuracySort?.direction || "default"}`;

  url += queryParams;

  const body = {
    questionGroupIds: qGids,
    skillIds: selectedSkills,
    assessmentIds: selectedAssessments,
    Type: selectedType,
    sortOptions: sortOptions.filter((option) => option.direction !== "default"),
  };

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  });

  const json = await res.json();
  return json;
};

export const getAllQuestionsExcludingQuestionnareId = async (token: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestion`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = await res.json();
  return json;
};

export const deleteQuestions = async (questions: string[], token: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestion`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(questions),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to delete question(s)");
  }
};

export const AssignQuestionsToTemplate = async (
  tId: string,
  questionIds: { questionId: string; questionTimesId: string }[],
  token: string
) => {
  await fetch(`${APIURL}/AssessmentQuestion/Assign/${tId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(questionIds),
  });
  return;
};

export const RemoveQuestionsFromTemplate = async (
  tId: string,
  questionIds: string[],
  token: string
) => {
  await fetch(`${APIURL}/AssessmentQuestion/Templates/Remove/${tId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(questionIds),
  });
  return;
};

export const RemoveQuestionsFromAssessment = async (
  aId: string,
  questionIds: string[],
  token: string
) => {
  await fetch(`${APIURL}/AssessmentQuestion/Assessments/Remove/${aId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(questionIds),
  });
  return;
};

export const getQuestionsByComprehensive = async (parentId: string, token: string) => {
  const res = await fetch(
    `${APIURL}/AssessmentQuestion/comprehensive/${parentId}/children`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const json = await res.json();
  return json;
};

export const CalculateAccuracy = async (token: string) => {
  await fetch(`${APIURL}/AssessmentQuestion/GenerateAccuracy`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return;
};

export const GetQuestionTimes = async (questionId: string, token: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestion/times/${questionId}`, {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  // The API returns 404 (not 200 + []) when a question has no time presets.
  // That is an empty result, not a failure - map it so callers don't treat it
  // as "load failed" and silently keep stale/empty state.
  if (res.status === 404) {
    return [];
  }

  if (!res.ok) {
    console.error(
      `GetQuestionTimes failed for ${questionId}: ${res.status} ${res.statusText}`
    );
    return;
  }

  const result = await res.json();
  return result;
};

export const AddQuestionTime = async (
  questionId: string,
  time: number,
  accuracy: number,
  isObsolete: boolean,
  token: string,
  questionDisplayTime?: number
) => {
  const res = await fetch(`${APIURL}/AssessmentQuestion/times/add`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      questionId: questionId,
      time: time,
      accuracy: accuracy,
      isObsolete: isObsolete,
      questionDisplayTime: questionDisplayTime,
    }),
  });

  if (!res.ok) {
    return;
  }

  const result = await res.json();
  return result;
};

export const UpdateIsObsolete = async (
  questionTimeId: string,
  isObsolete: boolean,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/AssessmentQuestion/times/${questionTimeId}/obsolete`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        isObsolete: isObsolete,
      }),
    }
  );

  if (!res.ok) {
    return;
  }

  const result = await res.json();
  return result;
};

export const UpdateQuestionTime = async (
  questionId: string,
  questionTimeId: string,
  time: number,
  questionDisplayTime: number | undefined,
  token: string
) => {
  const res = await fetch(
    `${APIURL}/AssessmentQuestion/times/${questionTimeId}`,
    {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        questionId, // Required by DTO but not used in update
        time: time,
        accuracy: 50, // Required by DTO but not updated
        isObsolete: false, // Required by DTO but not updated
        questionDisplayTime: questionDisplayTime,
      }),
    }
  );

  if (!res.ok) {
    return;
  }

  const result = await res.json();
  return result;
};

export const UpdateAccuracyForRoleAssessment = async (
  roleId: string,
  token: string
) => {
  const url = `${APIURL}/AssessmentQuestion/GenerateAccuracy/Role/${roleId}`;
  try {
    await fetch(url, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  } catch (err) {
    console.error("Error updating accuracy:", err);
  }
};

export const putAndPostAssessmentOptions = async (
  Qid: string | undefined,
  options: Option[],
  token: string
) => {
  let updatedOptions: Option[] = [];
  let newOptions: any[] = [];
  const resolvedOptions = await Promise.all(options);
  resolvedOptions.forEach((option, index) => {
    const { id, ...rest } = option;

    if (option.id.toLocaleString().length > 5) {
      updatedOptions.push({ ...option, order: index });
    } else {
      newOptions.push({ ...rest, questionId: Qid, order: index });
    }
  });
  if (updatedOptions.length > 0) {
    await fetch(`${APIURL}/AssessmentQuestionAnswer/put-multiple`, {
      method: "PUT",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(updatedOptions),
    });
  }

  if (newOptions.length > 0) {
    await fetch(`${APIURL}/AssessmentQuestionAnswer/post-multiple`, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(newOptions),
    });
  }
};

export const deleteAssessmentOptions = async (option: Option, token: string) => {
  await fetch(`${APIURL}/AssessmentQuestionAnswer`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(option),
  });
};

export const postQuestionGroup = async (title: string, token: string) => {
  await fetch(`${APIURL}/AssessmentQuestionGroup`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ title }),
  });
  return;
};

export const fetchQuestionGroups = async (token: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestionGroup`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
};


export const getAllQuestionsByQuestionnareId = async (id: string, token: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestionMapping/${id}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
};

export const addQuestionsToAssessment = async (assessmentQuestions: any, token: string) => {
  await fetch(`${APIURL}/AssessmentQuestionMapping/add-multiple`, {
    method: "POST",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(assessmentQuestions),
  });
  return;
};

export const removeQuestionsFromAssessment = async (
  assessmentQuestions: any,
  token: string
) => {
  await fetch(`${APIURL}/AssessmentQuestionMapping/delete-multiple`, {
    method: "DELETE",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(assessmentQuestions),
  });
  return;
};

// Re-points a question's mapping to a DIFFERENT, already-existing QuestionTimes
// preset (questionTimesId must be one of that question's own bank-level time
// options, e.g. from GetQuestionTimes) - scoped to this one assessment only,
// never changes that question's time in any other assessment that uses it.
export const updateQuestionMappingTime = async (
  questionnaireId: string,
  questionId: string,
  questionTimesId: string,
  token: string
) => {
  const res = await fetch(`${APIURL}/AssessmentQuestionMapping/update-time`, {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      questionnaireId,
      questionId,
      questionTimesId,
    }),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to update question time");
  }
  return res.json();
};

export const getQuestionOptionsById = async (qId: string) => {
  const res = await fetch(`${APIURL}/AssessmentQuestionAnswer/question/${qId}`, {
  });
  const json = res.json();
  return json;
}

export const whichAnswerFollowUp = async (questionId: string, token: string, optionId: string | null
) => {
  const res = await fetch(
    `${APIURL}/AssessmentQuestion/${questionId}/whichAnswerFollowUp`,
    {
      method: "PATCH",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(optionId),
    }
  );

  if (!res.ok) {
    throw new Error("Failed to update follow-up answer");
  }

  return res.json();
};



export async function GetCandidateRoleAnswers(
  candidateId: string,
  roleId: string,
  token: string
): Promise<CandidateRoleAnswerModel[]> {
  const res = await fetch(`${APIURL}/CandidateRoleAnswers/${candidateId}/${roleId}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  if (!res.ok) return [];
  const data = await res.json();
  const normalizedRoleId = roleId.toLowerCase();
  return data
    .filter((item: any) => {
      // Guard against mixed-role payloads by enforcing role match client-side.
      if (!item?.roleId) return true;
      return String(item.roleId).toLowerCase() === normalizedRoleId;
    })
    .map((item: any): CandidateRoleAnswerModel => ({
      questionId: item.questionId,
      answerId: item.answerId,
      questionText: item.question?.text ?? "",
      answerText: item.answer?.text ?? "",
    }));
}

export type CandidateRoleAnswersBulkResponse = Record<
  string,
  Record<
    string,
    {
      answerId?: string | null;
      answerText?: string | null;
      value?: string | null;
      valueNumber?: number | null;
    }
  >
>;

export async function GetCandidateRoleAnswersBulk(
  roleId: string,
  candidateIds: string[],
  questionIds: string[],
  token: string
): Promise<CandidateRoleAnswersBulkResponse> {
  const res = await fetch(`${APIURL}/CandidateRoleAnswers/bulk`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      roleId,
      candidateIds,
      questionIds,
    }),
  });
  if (!res.ok) return {};
  return await res.json();
}

export const importQuestionsFromJSON = async (
  file: File,
  token: string,
  assessmentId?: string
) => {
  const formData = new FormData();
  formData.append("file", file);

  let url = `${APIURL}/AssessmentQuestion/import`;
  if (assessmentId) {
    url += `?assessmentId=${assessmentId}`;
  }

  const res = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    throw new Error("Failed to import questions");
  }

  return res.ok;
};



export const uploadQuestionImage = async (file: File, token: string): Promise<string> => {
  const formData = new FormData();
  formData.append('file', file);

  const res = await fetch(`${APIURL}/AssessmentQuestion/upload-image`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
    },
    body: formData,
  });

  if (!res.ok) {
    throw new Error('Failed to upload image');
  }

  const json = await res.json();
  return json.imageUrl;
};
